/**
 * 通用导出工具（Excel）
 * 生成 Excel 2003 XML 工作簿，支持多工作表（报表批量导出「每期一个工作表」即靠它）。
 * 前端零依赖，Excel / WPS 均可直接打开。
 *
 * 用法：
 *   import { exportXls, exportCsv } from 'js/common/export'
 *   exportXls({ name: '明细账', sheets: [{ name: '1001 库存现金', head: ['日期','凭证号','摘要'], rows: [[...],[...]], widths: [90,80,200], types: ['s','s','s'] }] })
 *   exportCsv({ name: '科目余额表', head: [...], rows: [[...]] })
 */
function esc(s) {
	return String(s == null ? '' : s)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

function cellXml(v, styleId, type) {
	let t = type;
	let val = v;
	if (v == null || v === '') {
		return `<Cell${styleId ? ' ss:StyleID="' + styleId + '"' : ''}/>`;
	}
	if (t === 'n' || (t == null && typeof v === 'number')) {
		return `<Cell${styleId ? ' ss:StyleID="' + styleId + '"' : ''}><Data ss:Type="Number">${Number(v) || 0}</Data></Cell>`;
	}
	return `<Cell${styleId ? ' ss:StyleID="' + styleId + '"' : ''}><Data ss:Type="String">${esc(val)}</Data></Cell>`;
}

const STYLES = `<Styles>
<Style ss:ID="hd"><Font ss:Bold="1"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/><Interior ss:Color="#EEEEEE" ss:Pattern="Solid"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders></Style>
<Style ss:ID="ce"><Alignment ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders></Style>
<Style ss:ID="ri"><Alignment ss:Horizontal="Right" ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders></Style>
<Style ss:ID="total"><Font ss:Bold="1"/><Interior ss:Color="#FFF6E5" ss:Pattern="Solid"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/></Borders></Style>
</Styles>`;

/**
 * 导出 Excel（支持多 sheet）
 * @param {Object} opt {name, sheets:[{name,head,rows,widths,types,totalRow}]}
 */
export function exportXls(opt) {
	const sheets = opt.sheets || [];
	if (!sheets.length) return;
	const body = sheets.map(sh => {
		const widths = (sh.widths || []).map(w => `<Column ss:Width="${w}"/>`).join('');
		const types = sh.types || [];
		const aligns = sh.aligns || [];
		const headRow = `<Row>${(sh.head || []).map(h => `<Cell ss:StyleID="hd"><Data ss:Type="String">${esc(h)}</Data></Cell>`).join('')}</Row>`;
		const dataRows = (sh.rows || []).map(r => {
			const cells = (r || []).map((c, i) => {
				const sid = aligns[i] === 'right' ? 'ri' : 'ce';
				return cellXml(c, sid, types[i]);
			}).join('');
			return `<Row>${cells}</Row>`;
		}).join('');
		let totalRow = '';
		if (sh.totalRow) {
			totalRow = `<Row>${(sh.totalRow || []).map((c, i) => `<Cell ss:StyleID="total"><Data ss:Type="${typeof c === 'number' ? 'Number' : 'String'}">${esc(c)}</Data></Cell>`).join('')}</Row>`;
		}
		return `<Worksheet ss:Name="${esc((sh.name || 'Sheet').substring(0, 31))}"><Table>${widths}${headRow}${dataRows}${totalRow}</Table></Worksheet>`;
	}).join('');

	const xml = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
${STYLES}${body}</Workbook>`;

	const name = (opt.name || '导出') + '.xls';
	downloadBlob(name, xml, 'application/vnd.ms-excel;charset=utf-8');
	try { window.__lastXls = { name: name, content: xml, size: xml.length, t: Date.now() }; } catch (e) { /* ignore */ }
}

/** 供 e2e 读取最近一次导出的表格内容（绕开 headless blob 下载不可靠问题） */
export function lastXls() { return (typeof window !== 'undefined' && window.__lastXls) || null; }

/**
 * 导出 CSV（带 BOM，Excel 直接打开不乱码）
 */
export function exportCsv(opt) {
	const rows = [opt.head || []].concat(opt.rows || []);
	if (opt.totalRow) rows.push(opt.totalRow);
	const csv = rows.map(r => (r || []).map(c => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
	downloadBlob((opt.name || '导出') + '.csv', '\ufeff' + csv, 'text/csv;charset=utf-8');
}

/**
 * 解析页面上已渲染的表格 → {head, rows}
 * 自动处理：多级表头（用 - 拼接）、colspan/rowspan、千分位数字转数值、分组行/暂无数据行跳过
 * @param {String} id 表格或容器元素 id
 */
export function parseTable(id) {
	const el = document.getElementById(id);
	if (!el) return { head: [], rows: [] };
	const table = el.tagName === 'TABLE' ? el : el.querySelector('table');
	if (!table) return { head: [], rows: [] };

	const cellsOf = tr => Array.from(tr.children).filter(c => c.tagName === 'TD' || c.tagName === 'TH');
	const textOf = c => (c.innerText || c.textContent || '').trim();

	let headTrs = Array.from(table.querySelectorAll('thead tr'));
	if (!headTrs.length) {
		const first = table.querySelector('tr');
		if (first) headTrs = [first];
	}

	const grid = [];
	headTrs.forEach((tr, ri) => {
		grid[ri] = [];
		let ci = 0;
		cellsOf(tr).forEach(cell => {
			while (grid[ri][ci]) ci++;
			const cs = parseInt(cell.getAttribute('colspan') || '1', 10);
			const rs = parseInt(cell.getAttribute('rowspan') || '1', 10);
			const text = textOf(cell);
			for (let k = 0; k < cs; k++) {
				for (let r = 0; r < rs; r++) {
					if (!grid[ri + r]) grid[ri + r] = [];
					grid[ri + r][ci + k] = { text: text };
				}
			}
			ci += cs;
		});
	});

	let colCount = 0;
	grid.forEach(r => { if (r && r.length > colCount) colCount = r.length; });
	const head = [];
	for (let c = 0; c < colCount; c++) {
		const parts = [];
		for (let r = 0; r < grid.length; r++) {
			const cell = grid[r] && grid[r][c];
			if (cell && cell.text && parts.indexOf(cell.text) < 0) parts.push(cell.text);
		}
		head.push(parts.join('-'));
	}

	const rows = [];
	let bodyRows = Array.from(table.querySelectorAll('tbody tr'));
	if (!bodyRows.length) {
		let all = Array.from(table.querySelectorAll('tr'));
		bodyRows = all.filter(tr => headTrs.indexOf(tr) < 0);
	}
	bodyRows.forEach(tr => {
		const cells = cellsOf(tr);
		if (!cells.length) return;
		if (cells.length === 1 && (cells[0].getAttribute('colspan') || '1') !== '1') return;
		if (textOf(cells[0]) === '暂无数据' && cells.length <= 2) return;
		const row = [];
		cells.forEach(cell => {
			const cs = parseInt(cell.getAttribute('colspan') || '1', 10);
			let v = textOf(cell);
			const n = toNumber(v);
			row.push(n === null ? v : n);
			for (let k = 1; k < cs; k++) row.push('');
		});
		rows.push(row);
	});
	return { head: head, rows: rows };
}

function toNumber(s) {
	if (!s) return null;
	const t = String(s).replace(/,/g, '').replace(/¥/g, '').replace(/\s/g, '');
	if (!t) return null;
	if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
	if (t.indexOf('.') < 0 && t.length > 15) return null;
	return Number(t);
}

export function downloadBlob(name, content, mime) {
	const blob = new Blob([content], { type: mime });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = name;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
